<template>
	<KRSubHeaderLayout class="kr-sub-header">
		<template #end-date>
			<label class="stack-label">{{ translate( "dueLabel" ) }}</label>
			<h6 class="stack-value">{{ shortEndDate }}</h6>
		</template>
		<template #last-updated>
			<label class="stack-label">{{ translate( "lastUpdatedLabel" ) }}</label>
			<h6 class="stack-value">{{ lastUpdatedAgo }}</h6>
		</template>
		<template #confidence-level>
			<label class="stack-label">{{ translate( "confidenceLabel" ) }}</label>
			<div>
				<ConfidenceChip
					style="display: inline-flex;"
					:level="confidenceLevel"
					:disabled="true"
				/>
			</div>
		</template>
		<template #progress>
			<label class="stack-label">{{ translate( "progressAndPacingLabel" ) }}</label>
			<div class="progress-stack-value stack-value">
				<div>{{ progress }}%</div>
				<TeamingProgressBar
					class="progress-bar"
					:percent-completed="progress"
					:percent-progressed="pacing"
					:show-percent="false"
				/>
			</div>
		</template>
		<template #current>
			<label class="stack-label">{{ translate( "currentLabel" ) }}</label>
			<h6 class="stack-value">{{ formattedCurrentValue }}</h6>
		</template>
		<template #target>
			<label v-show="isTargetVisible" class="stack-label">{{ translate( "targetLabel" ) }}</label>
			<h6 v-show="isTargetVisible" class="stack-value">{{ formattedTargetValue }}</h6>
		</template>
	</KRSubHeaderLayout>
</template>

<script>
import { DateTime } from "luxon";

import {
	ConfidenceLevel,
	KeyResultType
} from "@teaming/common-enums";

import { KeyResultDisplayFormat } from "@teaming/common-gokr";

import {
	TeamingProgressBar,
	ConfidenceChip
} from "@teaming/common-ui";

import KRSubHeaderLayout from "./KRSubHeaderLayout";

export default {
	name: "KRSubHeader",
	emits: [ "back", ],
	components: {
		KRSubHeaderLayout,
		TeamingProgressBar,
		ConfidenceChip,
	},
	props: {
		// eslint-disable-next-line vue/no-unused-properties
		disabled: {
			type: Boolean,
			default: false,
		},
		endDate: {
			type: String,
			required: true,
		},
		lastUpdatedDate: {
			type: String,
			required: true,
		},
		confidenceLevel: {
			type: String,
			required: true,
			validator( value ) {
				return ConfidenceLevel.includes( value );
			},
		},
		progress: {
			type: Number,
			required: true,
		},
		pacing: {
			type: Number,
			required: true,
		},
		currentValue: {
			type: [ Number, String, ],
			required: true,
		},
		targetValue: {
			type: [ Number, String, ],
			required: true,
		},
		type: {
			type: String,
			required: true,
			validator( value ) {
				return KeyResultType.includes( value );
			},
		},
	},
	computed: {
		shortEndDate() {
			return DateTime.fromFormat( this.endDate, "MM/dd/yyyy" )
				.toFormat( "MMM d" ); // e.g. "Sep 30"
		},
		lastUpdatedAgo() {
			return DateTime.fromISO( this.lastUpdatedDate )
				.toRelative(); // e.g. "16 hours ago"
		},
		isTargetVisible() {
			return this.type !== KeyResultType.BINARY;
		},
		formattedCurrentValue() {
			return KeyResultDisplayFormat[ this.type ]( this.currentValue );
		},
		formattedTargetValue() {
			return KeyResultDisplayFormat[ this.type ]( this.targetValue );
		},
	},
	methods: {
		translate( key, args = {} ) {
			return this.$t( `common-gokr.components.KRSubHeader.${ key }`, args );
		},
	},
};
</script>

<style lang="scss" scoped>
$krSubHeaderLabelColor: $color1-500;
$krSubHeaderTextColor: $color1-700;
$krSubHeaderProgressColor: $color2-500;

.kr-sub-header .progress-bar {
	width: 100%;
}

.kr-sub-header .stack-label {
	@include font-and-line-for(-1);
	font-weight: 600;
	color: $krSubHeaderLabelColor;
	text-align: left;
}

.kr-sub-header .stack-value {
	@include font-and-line-for(0);
	font-weight: 600;
	color: $krSubHeaderTextColor;
	text-align: left;
}

.kr-sub-header .progress-stack-value {
	display: flex;
	flex-direction: row;
	gap: 0.5rem;

	color: $krSubHeaderProgressColor;
}
</style>
